// MC-Anzahl-Gate (Preisumbau Baender): `fetch-account-data` blockt NEUE
// Merchant Center ueber der Tarif-Grenze und haengt dafuer den
// maschinenlesbaren Marker `mc_limit_blocked:<n>` an die Sync-Abschlussmeldung
// in `google_accounts.sync_message` (Quelle: `supabase/functions/
// fetch-account-data/mc-gate.ts:withMcLimitMarker`).
//
// Dieses Modul ist die Frontend-Seite des Vertrags: Marker erkennen (parse),
// aus dem angezeigten Text entfernen (strip) und die erlaubte Gesamtzahl fuer
// die Hinweiszeile ausrechnen. Pure Funktionen, kein React.

/** Muster des Server-Markers. Global, damit strip auch Mehrfach-Vorkommen raeumt. */
const MC_LIMIT_MARKER_RE = /\s*mc_limit_blocked:(\d+)/;

/**
 * Anzahl der in diesem Sync geblockten Merchant Center, oder null wenn die
 * Meldung keinen Marker traegt (Normalfall, auch fuer alle Legacy-Meldungen).
 */
export function parseMcLimitBlocked(message: string | null | undefined): number | null {
  if (typeof message !== 'string') return null;
  const match = MC_LIMIT_MARKER_RE.exec(message);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Entfernt den Marker (samt fuehrendem Leerraum) aus der Anzeige-Meldung.
 * Meldungen ohne Marker laufen unveraendert durch — der Sync-Dialog zeigt
 * `sync_message` sonst woertlich, und der rohe Marker waere fuer den Nutzer
 * nur Rauschen.
 */
export function stripMcLimitMarker(message: string): string {
  return message.replace(new RegExp(MC_LIMIT_MARKER_RE.source, 'g'), '').trim();
}

/**
 * Inklusive Merchant Center nach Bandgroesse — SPIEGEL von
 * `supabase/functions/_shared/plan-features.ts:includedAllowance` (MC-Anteil).
 * Aenderungen dort muessen hier von Hand nachgezogen werden (gleiches Muster
 * wie `lib/scoreBreakdown.ts` gegenueber `_shared/scoring.ts`).
 *
 * Staffel (Entscheidung 5): 1 MC bis einschliesslich 2.000er-Band (und Free),
 * 3 fuer 5.000/10.000, unbegrenzt ab 25.000.
 */
export function includedMcAllowance(productLimit: number): number {
  if (!Number.isFinite(productLimit)) return Number.POSITIVE_INFINITY;
  if (productLimit >= 25_000) return Number.POSITIVE_INFINITY;
  if (productLimit >= 5_000) return 3;
  return 1;
}

/**
 * Erlaubte GESAMT-Anzahl Merchant Center: Inklusivmenge + gebuchte Add-ons
 * (`subscriptions.addon_mc_booked`). Der Server rechnet identisch
 * (`mc-gate.ts:buildMcGate`); die Zahl treibt die Hinweiszeile
 * "dein Tarif umfasst X".
 */
export function allowedMcTotal(
  productLimit: number,
  addonMcBooked: number | null | undefined,
): number {
  const included = includedMcAllowance(productLimit);
  if (!Number.isFinite(included)) return Number.POSITIVE_INFINITY;
  const addons = typeof addonMcBooked === 'number' && Number.isFinite(addonMcBooked)
    ? Math.max(0, Math.trunc(addonMcBooked))
    : 0;
  return included + addons;
}
